import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  pinRechargeField,
  pinTemporaryField,
  accountCodeField,
} from './SignupPDV.config';
import { requiredField } from 'utils/validation/required';
import { useLazySignupPDVQuery } from 'api/signUp/signUp';
import { useNavigate } from 'react-router-dom';
import { RoutePath } from 'router/router.config';
import { useAppDispatch, useAppSelector } from 'store/store.hooks';
import {
  selectRegistrationState,
  setRegistrationState,
} from 'store/slices/registration';
import mergeRegistrationStateWithPDVserviceResponse from './SignupPDV.helpers';
import { useModalControls } from 'components/Modal';
import { goTryRaw } from 'utils/functions/go-go-try';
import { sayError } from 'utils/functions/say';
import { useContactDataStepForm } from 'pages/ContactDataStep/ContactDataStep.hooks';
import { usePersonalDataStepForm } from 'pages/PersonalDataStep/PersonalDataStep.hooks';

export const useSignupPDVForm = () => {
  /*********************************************************
   *                     SERVICE HOOKS                     *
   ********************************************************/
  const [getSignupPDV] = useLazySignupPDVQuery();
  /*********************************************************
   *                      STORE HOOKS                      *
   ********************************************************/
  const dispatch = useAppDispatch();
  const registrationState = useAppSelector(selectRegistrationState);
  /*********************************************************
   *                     UTILITY HOOKS                     *
   ********************************************************/
  const navigate = useNavigate();
  const [showGenericErrorModal] = useModalControls(
    'SignupPdvGenericErrorModal',
  );
  const [showAccountNotFoundModal] = useModalControls(
    'SignupPdvAccountNotFoundModal',
  );
  /*********************************************************
   *                     FORM CONFIGS                      *
   ********************************************************/
  const signupPDVFormValidationSchema = z.object({
    [accountCodeField]: requiredField,
    [pinRechargeField]: requiredField,
    [pinTemporaryField]: requiredField,
  });

  const methods = useForm({
    defaultValues: {
      [accountCodeField]: '',
      [pinRechargeField]: '',
      [pinTemporaryField]: '',
    },
    resolver: zodResolver(signupPDVFormValidationSchema),
    mode: 'onChange',
  });
  /*********************************************************
   *                   FORM SUBMIT HANDLER                 *
   ********************************************************/
  const handleSubmit = methods.handleSubmit(async function (data) {
    /* -------------------------------------------------------------------------------------------------
     * SIGNUP PDV FLOW
     * -----------------------------------------------------------------------------------------------*/
    const [signupPdvError, signupPdvResponse] = await goTryRaw(
      getSignupPDV(data).unwrap(),
    );

    if (isAccountNotFoundError(signupPdvError)) {
      sayError('Signup pdv error, account not found');
      showAccountNotFoundModal();
      return;
    }

    if (signupPdvError || !signupPdvResponse) {
      sayError('Signup pdv error', signupPdvError);
      showGenericErrorModal();
      return;
    }

    dispatch(
      setRegistrationState(
        mergeRegistrationStateWithPDVserviceResponse(
          registrationState,
          signupPdvResponse,
          data,
        ),
      ),
    );

    navigate(RoutePath.Root);
  });
  /*********************************************************
   *                   STATUS & VARIABLES                  *
   ********************************************************/
  const isLoading = methods.formState.isSubmitting;

  return { methods, handleSubmit, isLoading };
};

function isAccountNotFoundError(signupPDVResponseError: any) {
  if (signupPDVResponseError?.status !== 422) return false;

  const error = signupPDVResponseError?.data.error;

  return (
    error?.type === 'VALIDATION' &&
    error?.errorInfos?.generic?.[0] === 'err.generic.account.not.found'
  );
}

export const usePreFilledPdv = () => {
  const { personalDataStep } = usePersonalDataStepForm();
  const { contactDataStep } = useContactDataStepForm();
  const { pdvCode } = useAppSelector(selectRegistrationState);

  const isPdvFlow = pdvCode?.fromPdv;

  return {
    isEmailPreFilled: isPdvFlow && !!contactDataStep.formValues.email,
    isConfirmEmailPreFilled: isPdvFlow && !!contactDataStep.formValues.confirmEmail,
    isFirstNamePreFilled: isPdvFlow && !!personalDataStep.formValues.firstName,
    isLastNamePreFilled: isPdvFlow && !!personalDataStep.formValues.lastName,
    isBirthDatePreFilled: isPdvFlow && !!personalDataStep.formValues.birthDate,
  };
};